'use client';

import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { useUserContext } from '@osmosis/nextjs-components/context/UserContext';
import { bgLightBlue } from '@osmosis/tailwind-ui/styles/background';
import {
  text0,
  text3,
  text4,
  text5,
  textBlue,
} from '@osmosis/tailwind-ui/styles/text';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import {
  BodyCtaArgs,
  TrialCtaArgs,
} from '../../lib/composer/schemas/answersValueProp';
import { getFontClass } from '../../lib/composer/schemas/font';
import { track } from '../../lib/util/tracking';
import TrialCTA from '../TrialCTA';

const SOURCE_CTA = 'answers-body-cta';

type Props = {
  /** Editable copy from the composer config; falls back to defaults below. */
  cta?: BodyCtaArgs;
  /** Unified Answer-page CTA copy. Omit to preserve legacy non-Answer usage. */
  trialCta?: TrialCtaArgs;
  sourceCTA?: string;
  trackingEvent?: string;
  classNames?: {
    wrapper?: string;
  };
};

/**
 * In-article "Start free trial" call to action shown below the first image
 * to logged-out users on answer pages.
 */
const AnswerBodyCTA = ({
  cta,
  trialCta,
  sourceCTA = SOURCE_CTA,
  trackingEvent = 'answers-cta-clicked',
  classNames,
}: Props) => {
  const { getPlansUrl } = useUserContext();

  const handleClick = () => {
    track(trackingEvent, { source_cta: sourceCTA });
  };

  const fontClass = getFontClass(cta?.fontStyle);

  return (
    <div
      data-testid="answer-body-cta"
      className={cn(
        'my-8 flex flex-col gap-4 rounded-lg border border-blue-600 p-6 dark:border-blue-400 md:flex-row md:items-center md:justify-between',
        bgLightBlue,
        classNames?.wrapper,
      )}
    >
      <div className="flex flex-col gap-2">
        <p
          className={cn(
            `text-xs font-bold uppercase tracking-wide ${textBlue}`,
            cta?.eyebrowFontSize,
            fontClass,
          )}
          data-testid="answer-body-cta-eyebrow"
        >
          {cta?.eyebrow ?? (
            <FormattedMessage
              id="answers.bodyCta.eyebrow"
              defaultMessage="Learn deeper with Osmosis"
            />
          )}
        </p>
        <p
          className={cn(
            `text-base font-bold ${text5}`,
            cta?.headlineFontSize,
            fontClass,
          )}
        >
          {cta?.headline ?? (
            <FormattedMessage
              id="answers.bodyCta.headline"
              defaultMessage="Master this topic faster with videos, questions, and AI."
            />
          )}
        </p>
        <p
          className={cn(
            `text-sm ${text4}`,
            cta?.reassuranceFontSize,
            fontClass,
          )}
        >
          {cta?.reassurance ?? (
            <FormattedMessage
              id="answers.bodyCta.reassurance"
              defaultMessage="Used by 8M+ healthcare learners."
            />
          )}
        </p>
      </div>

      {trialCta === undefined ? (
        <div className="flex shrink-0 flex-col items-center gap-2">
          <Button
            as="a"
            rounded
            href={getPlansUrl(`?source_cta=${sourceCTA}`)}
            onClick={handleClick}
            data-testid="answer-body-cta-button"
            className={cn(
              `${text0} px-14 py-1.5 max-md:w-full`,
              cta?.buttonLabelFontSize,
              fontClass,
            )}
          >
            {cta?.buttonLabel ?? (
              <FormattedMessage
                id="answers.bodyCta.button"
                defaultMessage="Start free trial"
              />
            )}
          </Button>
          <p
            data-testid="answer-body-cta-subtext"
            className={cn(
              `text-xs ${text3}`,
              cta?.fineprintFontSize,
              fontClass,
            )}
          >
            {cta?.fineprint ?? (
              <FormattedMessage
                id="answers.bodyCta.fineprint"
                defaultMessage="No credit card · Cancel anytime"
              />
            )}
          </p>
        </div>
      ) : (
        <TrialCTA
          copy={trialCta}
          onButtonClick={handleClick}
          buttonTestId="answer-body-cta-button"
          fineprintTestId="answer-body-cta-subtext"
          containerTestId="answer-body-trial-cta"
          classNames={{
            container: 'shrink-0 rounded-lg max-md:w-full',
            button: 'px-14 py-1.5 max-md:w-full',
          }}
        />
      )}
    </div>
  );
};

export default AnswerBodyCTA;
